/* eslint-disable react-hooks/immutability */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Users, FileText, Target, Clock } from 'lucide-react';
import api from '../../lib/api';
import KpiTooltip from '../shared/KpiTooltip';
import AgencyAIAnalysisCard from './AgencyAIAnalysisCard';

const fadeIn = { hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } };

const TYPE_LABELS = { simple: 'Simple', semi_exclusif: 'Semi-excl.', exclusif: 'Exclusif' };
const TYPE_COLORS = { simple: '#94a3b8', semi_exclusif: '#FF914D', exclusif: '#681793' };

export default function AgencyKPIs() {
    const [kpis, setKpis] = useState(null);
    const [period, setPeriod] = useState('6months');
    const [loading, setLoading] = useState(true);

    useEffect(() => { loadKPIs(); }, [period]);

    const loadKPIs = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/admin/agency/kpis?period=${period}`);
            setKpis(res.data);
        } catch { /* ignore */ } finally { setLoading(false); }
    };

    if (loading || !kpis) {
        return <div className="animate-pulse bg-card rounded-2xl h-64 border border-border" />;
    }

    const maxMonth = Math.max(...Object.values(kpis.mandates_by_month || {}).map(m => m.total), 1);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#FF914D]" />
                    KPIs détaillés
                </h2>
                <select value={period} onChange={e => setPeriod(e.target.value)} className="px-3 py-1.5 bg-card border border-border rounded-lg text-sm text-foreground">
                    <option value="3months">3 mois</option>
                    <option value="6months">6 mois</option>
                    <option value="12months">12 mois</option>
                </select>
            </div>

            {/* Top KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <KPICard label="Total mandats" value={kpis.total_mandates} icon={FileText} color="#681793" tip="Nombre total de mandats créés sur la période. Inclut tous les types (simple, semi-exclusif, exclusif)." />
                <KPICard label="Vendus" value={kpis.total_sold} icon={TrendingUp} color="#10b981" tip="Nombre de biens vendus (mandat passé au statut 'vendu'). C'est votre indicateur de performance commerciale." />
                <KPICard label="Taux conversion" value={`${kpis.conversion_rate}%`} icon={Target} color="#FF914D" tip="Pourcentage de mandats qui aboutissent à une vente. Formule : (vendus / total mandats) x 100. Un bon taux en immobilier se situe entre 20% et 40%." />
                <KPICard label="Délai moyen vente" value={kpis.avg_sell_time_days ? `${kpis.avg_sell_time_days}j` : '—'} icon={Clock} color="#8b5cf6" tip="Nombre moyen de jours entre la création du mandat et la vente. Plus ce délai est court, plus votre agence est efficace." />
            </div>

            {/* Type breakdown */}
            <div className="bg-card rounded-2xl border border-border p-5">
                <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                    Répartition par type de mandat
                    <KpiTooltip text="Répartition entre mandats exclusifs, semi-exclusifs et simples. Un taux élevé de mandats exclusifs (>50%) est un indicateur de confiance des vendeurs et de qualité de l'agence." />
                </h3>
                <div className="flex gap-4 mb-3">
                    {Object.entries(kpis.type_breakdown || {}).map(([type, count]) => (
                        <div key={type} className="flex-1 text-center">
                            <div className="text-2xl font-bold" style={{ color: TYPE_COLORS[type] }}>{count}</div>
                            <div className="text-xs text-muted-foreground">{TYPE_LABELS[type]}</div>
                            <div className="text-[10px] text-muted-foreground">{kpis.type_rates?.[type]}%</div>
                        </div>
                    ))}
                </div>
                {/* Bar */}
                <div className="h-3 rounded-full overflow-hidden flex bg-muted">
                    {Object.entries(kpis.type_rates || {}).map(([type, pct]) => (
                        <div key={type} style={{ width: `${pct}%`, backgroundColor: TYPE_COLORS[type] }} className="transition-all" />
                    ))}
                </div>
                {kpis.best_selling_type && (
                    <p className="text-xs text-muted-foreground mt-2">
                        Type le plus vendu : <span className="font-bold text-foreground">{TYPE_LABELS[kpis.best_selling_type]}</span>
                        {kpis.avg_sell_time_by_type?.[kpis.best_selling_type] && (
                            <span> (délai moyen : {kpis.avg_sell_time_by_type[kpis.best_selling_type]} jours)</span>
                        )}
                    </p>
                )}
                {(() => {
                    const total = Object.values(kpis.type_breakdown || {}).reduce((a, b) => a + b, 0);
                    const exclPct = total > 0 ? Math.round(((kpis.type_breakdown?.exclusif || 0) / total) * 100) : 0;
                    if (total === 0) return null;
                    return (
                        <div className={`mt-3 px-3 py-2 rounded-lg text-xs ${exclPct >= 50 ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'}`}>
                            {exclPct >= 50
                                ? `Excellent : ${exclPct}% de vos mandats sont en exclusivité. C'est un signe fort de confiance de vos vendeurs.`
                                : `${exclPct}% de mandats exclusifs. Objectif recommandé : >50% pour maximiser la rentabilité et réduire les délais de vente.`
                            }
                        </div>
                    );
                })()}
            </div>

            {/* Mandats par mois */}
            {Object.keys(kpis.mandates_by_month || {}).length > 0 && (
                <div className="bg-card rounded-2xl border border-border p-5">
                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                        Mandats par mois
                        <KpiTooltip text="Évolution mensuelle du nombre de mandats rentrés. Permet de visualiser les tendances saisonnières et la dynamique commerciale de l'agence." />
                    </h3>
                    <div className="flex items-end gap-2 h-32">
                        {Object.entries(kpis.mandates_by_month).map(([month, data]) => (
                            <div key={month} className="flex-1 flex flex-col items-center gap-1">
                                <span className="text-xs font-bold text-foreground">{data.total}</span>
                                <div className="w-full rounded-t-md bg-gradient-to-t from-[#681793] to-[#FF914D]" style={{ height: `${(data.total / maxMonth) * 100}%`, minHeight: '4px' }} />
                                <span className="text-[9px] text-muted-foreground">{month.slice(5)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Par agent - redesigned */}
            {kpis.mandates_by_agent?.length > 0 && (
                <div className="bg-card rounded-2xl border border-border p-5">
                    <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#681793]" />
                        Performance par agent
                        <KpiTooltip text="Détail individuel de chaque agent : mandats rentrés, exclusifs obtenus, ventes réalisées et mandats dénoncés. La barre de progression montre la part d'exclusifs (violet) vs. simples (gris). Le taux de conversion est le ratio ventes/mandats." />
                    </h3>
                    <div className="space-y-3">
                        {kpis.mandates_by_agent.map((agent, idx) => {
                            const convRate = agent.total > 0 ? Math.round(agent.sold / agent.total * 100) : 0;
                            return (
                                <div key={idx} className="p-3 rounded-xl bg-white/[0.03] border border-border/50 hover:bg-white/[0.05] transition-colors">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white text-xs font-bold">
                                                {agent.name?.split(' ').map(n => n[0]).join('')}
                                            </div>
                                            <span className="text-sm font-medium text-foreground">{agent.name}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${convRate > 30 ? 'bg-green-500/20 text-green-400' : convRate > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/5 text-white/30'}`}>
                                                {convRate}% conv.
                                            </span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-4 gap-2 text-center">
                                        <div>
                                            <p className="text-lg font-bold text-foreground">{agent.total}</p>
                                            <p className="text-[10px] text-muted-foreground">Mandats</p>
                                        </div>
                                        <div>
                                            <p className="text-lg font-bold text-[#681793]">{agent.exclusif}</p>
                                            <p className="text-[10px] text-muted-foreground">Exclusifs</p>
                                        </div>
                                        <div>
                                            <p className="text-lg font-bold text-green-400">{agent.sold}</p>
                                            <p className="text-[10px] text-muted-foreground">Ventes</p>
                                        </div>
                                        <div>
                                            <p className="text-lg font-bold text-red-400">{agent.denounced}</p>
                                            <p className="text-[10px] text-muted-foreground">Dénoncés</p>
                                        </div>
                                    </div>
                                    {/* Mini progress bar */}
                                    <div className="mt-2 h-1.5 rounded-full overflow-hidden flex bg-muted">
                                        {agent.exclusif > 0 && <div style={{ width: `${(agent.exclusif / agent.total) * 100}%` }} className="bg-[#681793]" />}
                                        {agent.simple > 0 && <div style={{ width: `${(agent.simple / agent.total) * 100}%` }} className="bg-slate-400" />}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Source des leads */}
            {Object.keys(kpis.lead_sources || {}).length > 0 && Object.keys(kpis.lead_sources).some(k => k !== 'non_renseigne') && (
                <div className="bg-card rounded-2xl border border-border p-5">
                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                        Source des leads
                        <KpiTooltip text="D'où viennent vos mandats ? Grâce à Homly, chaque mandat peut être tracé jusqu'à sa source d'acquisition. Permet d'optimiser vos investissements marketing et de concentrer vos efforts sur les canaux les plus rentables." />
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {Object.entries(kpis.lead_sources).filter(([k]) => k !== 'non_renseigne').map(([source, count]) => (
                            <div key={source} className="bg-white/5 rounded-lg p-3 text-center">
                                <div className="text-lg font-bold text-[#FF914D]">{count}</div>
                                <div className="text-xs text-muted-foreground capitalize">{source.replace(/_/g, ' ')}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* AI Summary */}
            <AgencyAIAnalysisCard />
        </div>
    );
}

function KPICard({ label, value, icon: Icon, color, tip }) {
    return (
        <motion.div variants={fadeIn} className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" style={{ color }} />
                <span className="text-xs text-muted-foreground">{label}{tip && <KpiTooltip text={tip} />}</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{value}</p>
        </motion.div>
    );
}
